/**
 * 자재 위젯 — 개요(dashboard) 에서 현재 현장에 들어간 자재를 한눈에 본다.
 *
 * 표시:
 *   - 헤더: "자재 N건"
 *   - KPI 라인: 종류 N · 총 X · 업체 M
 *   - 본문: 수량 desc 상위 5개 (이름 · 수량+단위 · 업체)
 *   - "자재 관리" 버튼 → 자재 관리 전용 페이지로 이동.
 */
import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Stack, Chip } from '@mui/material';
import { Inventory2 as InventoryIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import WidgetCard from '../WidgetCard';
import { SiteMaterialService, SiteMaterialSummary } from '../../../services/SiteMaterialService';

interface Props {
  projectId: number;
  onActionClick?: () => void;
}

const MaterialsWidget: React.FC<Props> = ({ projectId, onActionClick }) => {
  const navigate = useNavigate();
  const [summary, setSummary] = useState<SiteMaterialSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    SiteMaterialService.summary(projectId, 5)
      .then((s) => {
        if (!cancelled) setSummary(s);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : '조회 실패');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const handleAction = onActionClick ?? (() => navigate(`/project/${projectId}/materials`));
  const title = summary ? `자재 ${summary.item_count}건` : '자재';

  return (
    <WidgetCard
      title={title}
      icon={<InventoryIcon fontSize="small" />}
      actionLabel="자재 관리"
      onAction={handleAction}
      testId="widget-materials"
    >
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={20} />
        </Box>
      ) : error ? (
        <Typography variant="caption" color="error">
          {error}
        </Typography>
      ) : !summary || summary.item_count === 0 ? (
        <Typography variant="caption" color="text.secondary">
          등록된 자재가 없습니다. "자재 관리" 에서 첫 자재를 추가하세요.
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              variant="outlined"
              label={`종류 ${summary.item_count}`}
              sx={{ height: 22 }}
            />
            <Chip
              size="small"
              color="primary"
              label={`총 ${summary.total_quantity.toLocaleString('ko-KR')}`}
              sx={{ height: 22 }}
            />
            <Chip
              size="small"
              variant="outlined"
              label={`업체 ${summary.vendor_count}`}
              sx={{ height: 22 }}
            />
          </Stack>

          <Stack spacing={0.5}>
            {summary.top.map((m) => (
              <Box key={m.id} sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontWeight: 500,
                  }}
                  title={m.name}
                >
                  {m.name}
                </Typography>
                <Typography variant="body2" color="primary" sx={{ fontWeight: 700, flex: 'none' }}>
                  {m.quantity.toLocaleString('ko-KR')}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.25 }}>
                    {m.unit || '개'}
                  </Typography>
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    flex: 'none',
                    maxWidth: 80,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={m.vendor}
                >
                  {m.vendor}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Stack>
      )}
    </WidgetCard>
  );
};

export default MaterialsWidget;
